import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import * as ApprovalActions from '../../../store/actions/approval.action';
import * as HrActions from '../../../store/actions/hr.action';
import { getApprovalState } from '../../../store/selector/approval.selector';
import { getHrState } from '../../../store/selector/hr.selector';
import { columns } from './signatory-remarks-column';
import Services from '../../../shared/services';
import ConfirmationModal from '../../../shared/components/ui/modal/confirmation-modal';
import DynamicCrumbs from '../../../shared/components/ui/dashboard-layout/dynamic-crumbs';
import DynamicTabLink from '../../../shared/components/ui/dynamic-tabs/dynamic-tab-link';
import DynamicDataGrid from '../../../shared/components/ui/datagrid/datagrid';
import OptionsMenu from '../../../shared/components/ui/menu/options-menu';
import { IApprovalApplication, IDecide } from '../../../shared/interfaces/approval.interface';
import { IOptionsButton } from '../../../shared/interfaces/options.interface';
import { approvalActions } from '../../../shared/constants/approval-actions';
import { APPROVALS_TABS_CONSTANT } from '../../../shared/constants/approvals-tabs.constant';

const styles = {
  container: {
    background: '#fff',
    p: 4,
  },

  actionsButton: {
    padding: '0.5rem 1.5rem',
    borderRadius: '0.5rem',
    width: { sm: 'auto', xs: '100%' },
    mt: { sm: 0, xs: '2rem' },
  },
};

const ApprovalsSignatoryRemarks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  /* eslint-disable camelcase */
  const signatory = useSelector(getHrState);

  const {
    list: { items },
    selected,
    query,
  } = useSelector(getApprovalState);

  const { slug } = useParams<string>();

  const [data, setData] = useState<IApprovalApplication>();
  const [signatoryList, setSignatoryList] = useState<any>();
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<string>('');
  const [applicationStatus, setApplicationStatus] = useState<string>('');
  const [params, setParams] = useState<IDecide>({
    mode: '',
    remarks: '',
  });
  const [options, setOptions] = useState<Array<IOptionsButton> | undefined>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (slug) {
      const q = { ...query, q: slug };
      if (q) dispatch(ApprovalActions.ApprovalFetchApplication(q));
    }
  }, []);

  useEffect(() => {
    if (selected) {
      setData(selected.application);
    }
  }, [selected]);

  useEffect(() => {
    if (data) dispatch(HrActions.HrFetchApplication(data.slug));
  }, [data]);

  useEffect(() => {
    if (signatory.selected.application) {
      const {
        application: { signatory_remarks },
      } = signatory.selected;

      setSignatoryList(signatory_remarks);
    }
  }, [signatory.selected]);

  const renderActions = () => {
    if (data) {
      const { status } = selected;
      const { step } = data;

      if (status === 'created' && step === '1') {
        setOptions(approvalActions('withOngoing', onOpenModal, 'Approved'));
        setApplicationStatus('Approved');
      } else if (step === '3') {
        setOptions(approvalActions('withoutOngoing', onOpenModal, 'Completed'));
        setApplicationStatus('Completed');
      } else if (step === '4') {
        setOptions(approvalActions('withoutOngoing', onOpenModal, 'Finalized'));
        setApplicationStatus('Finalized');
      }
    } else {
      setOptions(approvalActions('withoutOngoing', onOpenModal, 'Approved'));
      setApplicationStatus('Approved');
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    renderActions();
    setAnchorEl(event.currentTarget);
  };

  const onOpenModal = (action: string) => {
    setMode(action);
    setModalOpen(true);
  };

  const onModalAction = (e: string, remarks?: string) => {
    if (e === 'confirm' && remarks) {
      setParams((prev: IDecide) => {
        return {
          ...prev,
          mode: mode,
          remarks: remarks,
        };
      });
    } else {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    if (params.mode) {
      onApproved();
    }
  }, [params]);

  useEffect(() => {
    renderActions();
  }, []);

  const onApproved = async () => {
    if (slug) {
      const { errors, success } = await Services.api.approval.decide(slug, params);

      if (success) {
        Services.toast('success', 'Application approved successfully');
        navigate('/approvals');
        setModalOpen(false);
      } else {
        const [{ message }] = errors;

        setModalOpen(false);
        Services.toast('error', message);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <DynamicCrumbs />

          {selected && (
            <Typography sx={{ fontSize: '1.5rem' }}>
              {selected.application.lastname}, {selected.application.firstname},{' '}
              {selected.application.middlename} - {selected.application?.code}
            </Typography>
          )}
        </Box>
        {data && (
          <Box>
            <Button
              variant='contained'
              sx={styles.actionsButton}
              disableElevation
              onClick={handleClick}
              endIcon={<KeyboardArrowDownIcon />}
            >
              Actions
            </Button>

            <OptionsMenu
              open={open}
              handleClose={handleClose}
              anchorEl={anchorEl}
              optionItems={options}
            />
          </Box>
        )}
      </Box>

      <DynamicTabLink tabs={APPROVALS_TABS_CONSTANT} />

      <Box sx={{ my: 2 }}>
        <Box sx={{ background: '#fff', height: '80vh' }}>
          {signatoryList && <DynamicDataGrid rows={signatoryList} columns={columns} />}
        </Box>
      </Box>

      <ConfirmationModal
        isModalOpen={modalOpen}
        title={'Confirmation'}
        message={`The status of this application will be set to "${
          mode === 'ongoing' ? 'Ongoing' : mode === 'rejected' ? 'Rejected' : applicationStatus
        }". Are you sure you want to proceed?`}
        isRemarks={true}
        onClickModal={(e: string, remarks?: string) => onModalAction(e, remarks)}
      />
    </>
  );
};
export default ApprovalsSignatoryRemarks;
